import { DateTime } from 'luxon'

/**
 * Date-range math for the availability domain. Every range in this domain is encoded as the
 * bracketed string `[startISO, endISO]` — this module owns building, parsing and splitting that
 * encoding so the store, the engine's keys, and the calendar all agree on one implementation
 * (there used to be six near-verbatim copies of the two functions below, and any drift between
 * them silently changes cache-key identity).
 */

const RANGE_PATTERN = /\[(.*?),\s*(.*?)\]/

export function isTodayOrLater(isoDate: string): boolean {
  return DateTime.fromISO(isoDate).startOf('day') >= DateTime.now().startOf('day')
}

// The `[startISO, endISO]` bounds of a range string, or undefined if it isn't one.
export function parseRange(dateRange: string): [DateTime, DateTime] | undefined {
  const match = dateRange.match(RANGE_PATTERN)
  if (!match) return undefined
  return [DateTime.fromISO(match[1]!), DateTime.fromISO(match[2]!)]
}

/**
 * The `[start-of-month, end-of-month]` range around `center`.
 *
 * - `overflowDays` extends the end into the following month by that many days (the calendar shows
 *   next month's leading days as overflow tiles on the last row, and they need availability too).
 * - `maxDate` clamps the end (the `numDaysToShow` upper bound); it wins over the overflow
 *   extension, never widens the range.
 */
export function monthRangeFor(
  center: DateTime,
  { overflowDays = 0, maxDate }: { overflowDays?: number; maxDate?: DateTime } = {}
): string {
  const start = center.startOf('month').startOf('day')
  const monthEnd = center.endOf('month').endOf('day')

  const extendedEnd = overflowDays > 0
    ? center.plus({ months: 1 }).set({ day: overflowDays }).endOf('day')
    : monthEnd

  const end = maxDate ? DateTime.min(maxDate.endOf('day'), extendedEnd) : extendedEnd

  return `[${start.toISO()}, ${end.toISO()}]`
}

// The tight `[start-of-day, end-of-day]` range a single date's times fetch is scoped to.
export function dayRangeFor(isoDate: string): string {
  const date = DateTime.fromISO(isoDate)
  return `[${date.startOf('day').toISO()}, ${date.endOf('day').toISO()}]`
}

/**
 * Splits a month range into weekly chunks.
 *
 * A chunk that falls entirely in the past is dropped, and a chunk that merely *starts* in the past
 * has its start clamped to today — so a mid-month visit never keys (or fetches) days that can no
 * longer be booked.
 *
 * KNOWN: because of that clamp this folds `DateTime.now()` into the strings it returns, so a
 * session that crosses midnight re-keys its current week. Left as-is deliberately — it is a
 * key-identity concern and belongs with the key work in the next step of this refactor.
 */
export function splitMonthIntoWeeks(dateRange: string, minDate?: string): string[] {
  const bounds = parseRange(dateRange)
  if (!bounds) return []

  const [rangeStart, endDate] = bounds
  const startDate = rangeStart.startOf('day')
  const now = DateTime.now().startOf('day')
  const floor = minDate ? DateTime.max(now, DateTime.fromISO(minDate).startOf('day')) : now

  const weeks: string[] = []
  let currentChunkStart = startDate

  while (currentChunkStart <= endDate) {
    const chunkEnd = DateTime.min(currentChunkStart.plus({ days: 6 }).endOf('day'), endDate)

    if (chunkEnd >= floor) {
      const adjustedStart = currentChunkStart < floor ? floor : currentChunkStart
      weeks.push(`[${adjustedStart.toISO()}, ${chunkEnd.toISO()}]`)
    }

    currentChunkStart = currentChunkStart.plus({ days: 7 }).startOf('day')
  }

  return weeks
}

// Whether a `[startISO, endISO]` range covers the given instant.
export function rangeContains(dateRange: string, date: DateTime): boolean {
  const bounds = parseRange(dateRange)
  if (!bounds) return false
  return date >= bounds[0] && date <= bounds[1]
}

/**
 * The weeks a dates scope spans: `dateRange`'s weeks, plus the weeks of `selectedDate`'s whole
 * month when — and only when — no week already covers the selected date. That keeps the selected
 * date's week in scope after the calendar navigates to a different month, which is the whole point
 * of the union.
 *
 * The guard is COVERAGE of the selected date, not string-equality of the two week lists. It used
 * to be the latter, on the reasoning that a selected date inside `dateRange`'s month makes both
 * sides produce identical strings. That is false whenever the visible range carries overflow days
 * or a `maxDate` clamp, because the two sides are chunked from different bounds and diverge on the
 * LAST week:
 *
 *     visible (overflowDays: 5)   … [Jul 22, Jul 28]  [Jul 29, Aug 4]  [Aug 5, Aug 5]
 *     monthRangeFor(selectedDate) … [Jul 22, Jul 28]  [Jul 29, Jul 31]
 *                                                      ^^^^^^^^^^^^^^ not in the visible set
 *
 * `monthRangeFor` is deliberately called bare here — `overflowDays` describes the CENTER month's
 * calendar grid, so it must not be applied to some other month — which means the divergence cannot
 * be fixed by matching the options. Under the old string dedupe that last week entered the scope as
 * a phantom key covering days the visible entry already had data for: the calendar skeletoned them,
 * `datesLoading` went true and blocked the time picker, and the engine fetched a week it already
 * held. Selecting a date in the current month is now a no-op for the dates scope, as intended.
 */
export function weekRangesFor(dateRange: string, selectedDate?: string, minDate?: string): string[] {
  const weeks = splitMonthIntoWeeks(dateRange, minDate)
  if (!selectedDate) return weeks

  const selected = DateTime.fromISO(selectedDate).startOf('day')
  if (weeks.some((weekRange) => rangeContains(weekRange, selected))) return weeks

  const selectedMonthWeeks = splitMonthIntoWeeks(monthRangeFor(selected), minDate)
  const seen = new Set(weeks)
  return [...weeks, ...selectedMonthWeeks.filter((weekRange) => !seen.has(weekRange))]
}
